import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRAZILIAN_STATES } from "@/lib/constants/brazilian-states";

interface ProfissionalDadosPessoaisProps {
  professionalId: string;
}

export function ProfissionalDadosPessoais({ professionalId }: ProfissionalDadosPessoaisProps) {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [healthAreas, setHealthAreas] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    full_name: "",
    cpf: "",
    phone: "",
    health_area: "",
    address_zipcode: "",
    address_street: "",
    address_number: "",
    address_complement: "",
    address_neighborhood: "",
    address_city: "",
    address_state: "",
  });

  useEffect(() => {
    loadData();
    loadHealthAreas();
  }, [professionalId]);

  const loadHealthAreas = async () => {
    const { data } = await supabase
      .from("health_areas")
      .select("id, name")
      .eq("is_active", true)
      .order("display_order");
    setHealthAreas(data || []);
  };

  const loadData = async () => {
    try {
      setLoadingData(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", professionalId)
        .single();

      if (error) throw error;
      if (data) {
        setFormData({
          full_name: data.full_name || "",
          cpf: data.cpf || "",
          phone: data.phone || "",
          health_area: data.health_area || "",
          address_zipcode: data.address_zipcode || "",
          address_street: data.address_street || "",
          address_number: data.address_number || "",
          address_complement: data.address_complement || "",
          address_neighborhood: data.address_neighborhood || "",
          address_city: data.address_city || "",
          address_state: data.address_state || "",
        });
      }
    } catch (error: any) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoadingData(false);
    }
  };

  const fetchAddressByCep = async (cep: string) => {
    try {
      const cleanCep = cep.replace(/\D/g, "");
      if (cleanCep.length !== 8) return;

      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }

      setFormData(prev => ({
        ...prev,
        address_street: data.logradouro || "",
        address_neighborhood: data.bairro || "",
        address_city: data.localidade || "",
        address_state: data.uf || "",
      }));
    } catch (error) {
      toast.error("Erro ao buscar CEP");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update(formData)
        .eq("id", professionalId);

      if (error) throw error;

      toast.success("Dados atualizados com sucesso");
    } catch (error: any) {
      toast.error("Erro ao atualizar dados");
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <div>Carregando...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2 col-span-2">
          <Label htmlFor="full_name">Nome Completo</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="cpf">CPF</Label>
          <Input
            id="cpf"
            value={formData.cpf}
            onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Telefone</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>

        <div className="space-y-2 col-span-2">
          <Label htmlFor="health_area">Área de Saúde</Label>
          <Select
            value={formData.health_area}
            onValueChange={(value) => setFormData({ ...formData, health_area: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a área" />
            </SelectTrigger>
            <SelectContent>
              {healthAreas.map((area) => (
                <SelectItem key={area.id} value={area.name}>
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_zipcode">CEP</Label>
          <Input
            id="address_zipcode"
            value={formData.address_zipcode}
            onChange={(e) => {
              setFormData({ ...formData, address_zipcode: e.target.value });
              if (e.target.value.length === 8) fetchAddressByCep(e.target.value);
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_street">Rua</Label>
          <Input
            id="address_street"
            value={formData.address_street}
            onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_number">Número</Label>
          <Input
            id="address_number"
            value={formData.address_number}
            onChange={(e) => setFormData({ ...formData, address_number: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_complement">Complemento</Label>
          <Input
            id="address_complement"
            value={formData.address_complement}
            onChange={(e) => setFormData({ ...formData, address_complement: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_neighborhood">Bairro</Label>
          <Input
            id="address_neighborhood"
            value={formData.address_neighborhood}
            onChange={(e) => setFormData({ ...formData, address_neighborhood: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_city">Cidade</Label>
          <Input
            id="address_city"
            value={formData.address_city}
            onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_state">Estado</Label>
          <Select
            value={formData.address_state}
            onValueChange={(value) => setFormData({ ...formData, address_state: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o estado" />
            </SelectTrigger>
            <SelectContent>
              {BRAZILIAN_STATES.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button type="submit" disabled={loading}>
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Salvar Alterações
      </Button>
    </form>
  );
}